import { useQuery } from '@tanstack/react-query';
import { useShellSearchParams } from '../lib/use-shell-search-params';
import { useLocale } from '../i18n/locale-context';
import {
  fetchKindCounts,
  parseKindParam,
  type ProcurementKind,
  parseStatusParam,
  parseIntParam,
  parseIsoDateParam,
} from '../lib/catalog-client';
import { useSearchFacets } from '../lib/use-search-facets';
import { TabStrip, type TabStripItem } from './tab-strip';

const TABS: Array<{ value: '' | ProcurementKind; labelKey: string }> = [
  { value: '', labelKey: 'catalog.kind.all' },
  { value: 'goods', labelKey: 'catalog.kind.goods' },
  { value: 'works', labelKey: 'catalog.kind.works' },
  { value: 'services', labelKey: 'catalog.kind.services' },
];

/**
 * Вкладки типа закупки: Все · Товары · Работы · Услуги. Управляют параметром
 * `kind` в URL — всё состояние в адресной строке, как у остальных фильтров.
 *
 * Счётчики обязательны, а не декоративны: пустые пересечения реальны (категория
 * «химия» + «работы» = 0 живых), и без числа пользователь жмёт вкладку, видит
 * пустой список и решает, что сайт сломан. Считаются при ТЕКУЩИХ остальных
 * фильтрах — регион/категория/ниша/заказчик/минус-слова/статус/бюджет/дедлайн/
 * `since` — поэтому меняются вместе с ними.
 *
 * Пока активен свободный текстовый поиск (`q`), счётчики НЕ запрашиваются и
 * числа не рендерятся вовсе (вкладки остаются кликабельными, просто без
 * цифры): список при `q` ранжируется гибридным семантика+ключевые слова
 * поиском с порогом релевантности, поэтому точное число тут физически не
 * посчитать — `GET /catalog/kind-counts` тихо игнорирует `q` (считает по
 * обычному `buildWhere`), и отправка `q` только создавала врущие счётчики.
 */
export function KindTabs({
  scope,
  source,
  status: statusOverride,
  awarded,
  activeTotal,
}: {
  scope?: string;
  /** Площадка основного каталога (`?source=`, kaztender.com), уже проверенная
   *  страницей по `govSources` страны — чтобы числа вкладок считали тот же
   *  набор, что и список под ними. */
  source?: string;
  status?: string;
  awarded?: boolean;
  /**
   * The page's own list total for the CURRENT filters, shown on the ACTIVE
   * tab instead of the counts endpoint's number. The list and the counts are
   * two requests, and on the catalogue the list comes from the prerendered
   * snapshot (kept on mount to avoid a layout shift) while the counts are
   * live — so «Все 28 186» stood beside «29 404 тендера». The caller passes it
   * only when the list counts the same set the tabs do; undefined keeps the
   * endpoint's number. Also known at SSR, so the active tab's count is in the
   * prerendered HTML instead of popping in after hydration.
   */
  activeTotal?: number;
} = {}) {
  const { t } = useLocale();
  const [params, setParams] = useShellSearchParams();
  const active = parseKindParam(params.get('kind')) ?? '';

  const region = params.get('region') ?? undefined;
  const category = params.get('category') ?? undefined;
  const ikpu = params.get('ikpu') ?? undefined;
  const niche = params.get('niche') ?? undefined;
  // The pinned buyer and the minus words narrow the list, so they narrow these
  // counts too (the endpoint's `buildWhere` applies both) — without them the
  // tabs counted a wider set than the list under them.
  const customer = params.get('customer') ?? undefined;
  const exclude = params.get('exclude') ?? undefined;
  // `/results` imposes closed+awarded by ROUTE, not by URL param, so the page
  // has to hand it down or these badges count the open catalogue instead.
  const status = statusOverride ?? parseStatusParam(params.get('status'));
  const q = params.get('q') ?? undefined;
  // Guarded exactly like `kind` above: an unrecognised value is "no filter",
  // never passed through to 400 the counts request.
  const minBudgetRaw = parseIntParam(params.get('minBudget'));
  const maxBudgetRaw = parseIntParam(params.get('maxBudget'));
  const deadlineFrom = parseIsoDateParam(params.get('deadlineFrom'));
  const deadlineTo = parseIsoDateParam(params.get('deadlineTo'));
  const since = parseIsoDateParam(params.get('since'));
  const minBudget = minBudgetRaw !== undefined ? Number(minBudgetRaw) : undefined;
  const maxBudget = maxBudgetRaw !== undefined ? Number(maxBudgetRaw) : undefined;

  // While a query is active the plain kind-counts endpoint would lie (it
  // ignores `q`), so it's disabled below; the counts instead come from the
  // search facets (`useSearchFacets`), which count over the SAME hybrid match
  // set the results do. Not on `/results` (awarded archive), which the search
  // facets don't model — there the tabs stay uncounted, as before.
  const hasQuery = Boolean(q);
  const useFacetCounts = hasQuery && !awarded;
  // `uncounted`: a budget bound or a platform narrows the list but not the
  // facets — those tabs then carry no number (the active one keeps the list's
  // own `activeTotal`), never one for a different set.
  const { facets, uncounted } = useSearchFacets();

  const { data: counts } = useQuery({
    queryKey: [
      'catalog',
      'kind-counts',
      {
        region,
        category,
        ikpu,
        niche,
        customer,
        exclude,
        status,
        scope,
        source,
        awarded,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
      },
    ],
    queryFn: () =>
      fetchKindCounts({
        region,
        category,
        ikpu,
        niche,
        customer,
        exclude,
        status,
        scope,
        source,
        awarded,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
      }),
    enabled: !hasQuery,
  });

  function select(value: '' | ProcurementKind) {
    const next = new URLSearchParams(params);
    if (value) next.set('kind', value);
    else next.delete('kind');
    next.delete('page'); // смена вкладки сбрасывает пагинацию
    setParams(next);
  }

  function countFor(value: '' | ProcurementKind): number | undefined {
    if (value === active && activeTotal != null) return activeTotal;
    // During a search, the per-kind numbers come from the search facets (the
    // `kind.all` there is the grand total, kind-less lots included — the same
    // number the «Все» tab wants).
    if (useFacetCounts) {
      const k = uncounted ? undefined : facets?.kind;
      if (!k) return undefined;
      return value === '' ? k.all : k[value];
    }
    if (!counts) return undefined;
    return value === '' ? counts.all : counts[value];
  }

  const items: TabStripItem<'' | ProcurementKind>[] = TABS.map((tab) => {
    const count = countFor(tab.value);
    const label = t(tab.labelKey);
    return {
      value: tab.value,
      label,
      count,
      // Пока счётчики грузятся — просто метка, без фразы с пустым числом.
      ariaLabel: count != null ? t('catalog.kind.ariaLabel', { label, count }) : label,
    };
  });

  return (
    <TabStrip ariaLabel={t('catalog.kind.aria')} items={items} active={active} onSelect={select} />
  );
}
